import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import { DATA_TABLE_SOURCE_UTILITY_MAPPING_HITLIST } from 'common/dataTableConstants';

import {
  PARTNER_ADDRESS_STAGE_TO_LABEL_MAPPING,
  UTILITY_PARTNER_STATUS_PENDING_DEMO,
  UTILITY_PARTNER_STATUS_ONBOARDED,
  UTILITY_PARTNER_STATUS_ACTIVE,
  UTILITY_PARTNER_STATUS_CHURNED,
  UTILITY_PARTNER_STATUS_ARCHIVED,
  PARTNER_ADDRESS_STAGES_LIST,
} from 'common/utility/constants';

import { stateCodeToStateNameMapping } from 'common/util/stateCodes';

import {
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_VERIFICATION_START,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_VERIFICATION_END,
} from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import UPMappingHitlistRow from 'client/components/UPMappingHitlist/UPMappingHitlistRow';
import UPDataTableMappingDefaultsCollapsible from 'client/components/UPDataTableMappingDefaults/UPDataTableMappingDefaultsCollapsible';
import UPAdminDropdownButton from 'client/components/UPAdminDropdownButton/UPAdminDropdownButton';

const SORT_MODE_PRIORITY = 'priority';

const sortModeToSortMapping = {
  [SORT_MODE_PRIORITY]: [
    {
      assignedAdminUserId: 'asc',
    },
    {
      processingStartedAt: 'desc',
    },
    {
      stage: 'desc',
    },
    {
      listingLeasedAt: 'desc',
    },
    {
      listingUnlistedAt: 'desc',
    },
    {
      createdAt: 'asc',
    },
  ],
};

const stageOptions = [
  {
    value: undefined,
    label: 'All',
  },
  ...PARTNER_ADDRESS_STAGES_LIST.map((value) => ({
    value: value,
    label: PARTNER_ADDRESS_STAGE_TO_LABEL_MAPPING[value],
  })),
];

const stateOptions = [
  {
    value: undefined,
    label: 'All',
  },
  ...Object.keys(stateCodeToStateNameMapping).map((stateCode) => ({
    value: stateCode,
    label: stateCodeToStateNameMapping[stateCode],
  })),
];

const MAPPING_PROGRESS_NEED_VERIFICATION = 'need_verification';
const MAPPING_PROGRESS_NEED_MAPPING = 'need_mapping';

const mappingProgressOptions = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: MAPPING_PROGRESS_NEED_VERIFICATION,
    label: 'Need Verification',
  },
  {
    value: MAPPING_PROGRESS_NEED_MAPPING,
    label: 'Need Mapping',
  },
];

const partnerStatusOptions = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: UTILITY_PARTNER_STATUS_ACTIVE,
    label: 'Active',
  },
  {
    value: UTILITY_PARTNER_STATUS_PENDING_DEMO,
    label: 'Pending Demo',
  },
  {
    value: UTILITY_PARTNER_STATUS_CHURNED,
    label: 'Churned',
  },
  {
    value: UTILITY_PARTNER_STATUS_ARCHIVED,
    label: 'Archived',
  },
];

const partnerStatusToLabelMapping = partnerStatusOptions.reduce((memo, option) => {
  memo[option.value] = option.label;
  return memo;
}, {});

const UPMappingHitlist = ({ currentUser, dispatch, utilityTypes }) => {
  const [hitlistsExpanded, setHitlistsExpanded] = useState({});
  const [stageFilter, setStageFilter] = useState(null);
  const [stateFilter, setStateFilter] = useState(null);
  const [utilityTypeFilter, setUtilityTypeFilter] = useState(null);
  const [mappingProgressFilter, setMappingProgressFilter] = useState(null);
  const [partnerStatusFilter, setPartnerStatusFilter] = useState(UTILITY_PARTNER_STATUS_ACTIVE);
  const excludedFilterKeysFromSearch = ['partnerStatus', 'partnerCustomerType'];
  const initialDataTableFilters = {
    partnerStatus: [UTILITY_PARTNER_STATUS_ACTIVE, UTILITY_PARTNER_STATUS_ONBOARDED],
  };

  const utilityTypeOptions = [
    {
      value: undefined,
      label: 'All',
    },
    ...utilityTypes.map((utilityType) => ({
      value: utilityType.id,
      label: utilityType.label,
    })),
  ];

  const utilityTypeMapping = utilityTypes.reduce((memo, utilityType) => {
    memo[utilityType.id] = utilityType.label;
    return memo;
  }, {});

  return (
    <SrDataTableConnected
      dataSource={DATA_TABLE_SOURCE_UTILITY_MAPPING_HITLIST}
      rootViewId={DATA_TABLE_SOURCE_UTILITY_MAPPING_HITLIST}
      initialViewSettings={{
        sort: sortModeToSortMapping[SORT_MODE_PRIORITY],
        filter: initialDataTableFilters,
        fuzzy: null,
      }}
      count={30}
      itemsPropName="hitlistTasks"
      resetOnUnmount
    >
      {({
        hitlistTasks,
        reloadItems,
        dataTableApiState,
        viewSettings,
        onInfiniteScrollWrapperRef,
        isLoading,
      }) => {
        const reloadHitlistTask = (hitlistTaskId) => {
          reloadItems({
            count: 1,
            filter: {
              id: hitlistTaskId,
            },
          });
        };

        const onButtonClick = ({ hitlistTask }) => {
          const isInProgress = !!(
            hitlistTask.partnerAddress.processingStartedAt &&
            !hitlistTask.partnerAddress.processingEndedAt
          );

          if (!isInProgress) {
            return dispatch(requestUtilityApi, {
              action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_VERIFICATION_START,
              payload: {
                utilityPartnerAddressId: hitlistTask.partnerAddress.id,
              },
              onError(error) {
                reloadHitlistTask(hitlistTask.id);
                global.alert(error.message);
              },
              onSuccess() {
                reloadHitlistTask(hitlistTask.id);
              },
            });
          }

          if (hitlistTask.assignedAdminUserId !== currentUser.id) {
            return;
          }

          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_VERIFICATION_END,
            payload: {
              utilityPartnerAddressId: hitlistTask.partnerAddress.id,
            },
            onError(error) {
              reloadHitlistTask(hitlistTask.id);
              global.alert(error.message);
            },
            onSuccess() {
              // HACK(@d-oliveros): The item is not being removed automatically, and there is no way to reset the DataTable easily yet.
              dispatch(({ state }) => {
                const dataSource = DATA_TABLE_SOURCE_UTILITY_MAPPING_HITLIST;
                const statePath = ['dataTables', dataSource, 'items'];
                state.set(
                  statePath,
                  state.get(statePath).filter((item) => item.id !== hitlistTask.id),
                );
              });
            },
          });
        };

        return (
          <div>
            <SrBox paddingBottom="10px">
              <SrFlex columnGap="10px" align="baseline">
                <SrBox marginBottom="-18px" display="flex" flexDirection="row">
                  <UPAdminDropdownButton
                    title={`State: ${
                      stateFilter ? stateCodeToStateNameMapping[stateFilter] : 'All'
                    }`}
                    onClickOption={(stateValue) => {
                      setStateFilter(stateValue);
                      viewSettings.update({
                        ...(viewSettings || {}),
                        filter: {
                          ...viewSettings.filter,
                          addressStateCode: stateValue,
                        },
                      });
                    }}
                    options={stateOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                      selected: option.value === stateFilter,
                    }))}
                    minWidth="100px"
                    maxWidth="190px"
                    boxShadow="none"
                    className="dataTableFilter"
                  />
                  <UPAdminDropdownButton
                    title={`Listing Stage: ${
                      stageFilter ? PARTNER_ADDRESS_STAGE_TO_LABEL_MAPPING[stageFilter] : 'All'
                    }`}
                    onClickOption={(stageValue) => {
                      setStageFilter(stageValue);
                      viewSettings.update({
                        ...(viewSettings || {}),
                        filter: {
                          ...viewSettings.filter,
                          stage: stageValue ? parseInt(stageValue, 10) : stageValue,
                        },
                      });
                    }}
                    options={stageOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                      selected: option.value === stageFilter,
                    }))}
                    minWidth="150px"
                    maxWidth="230px"
                    boxShadow="none"
                    className="dataTableFilter"
                  />
                  <UPAdminDropdownButton
                    title={`Utility Type: ${
                      utilityTypeFilter ? utilityTypeMapping[utilityTypeFilter] : 'All'
                    }`}
                    onClickOption={(utilityTypeValue) => {
                      setUtilityTypeFilter(utilityTypeValue);
                      viewSettings.update(
                        {
                          ...(viewSettings || {}),
                          customParams: {
                            missingMappingUtilityTypeId: utilityTypeValue,
                          },
                        },
                        true,
                      );
                    }}
                    options={utilityTypeOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                      selected: option.value === utilityTypeFilter,
                    }))}
                    className="dataTableFilter"
                  />
                  <UPAdminDropdownButton
                    title={`Mapping Progress: ${
                      mappingProgressFilter
                        ? mappingProgressFilter === MAPPING_PROGRESS_NEED_VERIFICATION
                          ? 'Need Verification'
                          : 'Need Mapping'
                        : 'All'
                    }`}
                    options={mappingProgressOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                      selected: option.value === mappingProgressFilter,
                    }))}
                    onClickOption={(mappingProgressValue) => {
                      setMappingProgressFilter(mappingProgressValue);

                      const mappingProgressFilter = {};
                      if (mappingProgressValue === MAPPING_PROGRESS_NEED_VERIFICATION) {
                        mappingProgressFilter.needsMappingConfirmation = true;
                      } else if (mappingProgressValue === MAPPING_PROGRESS_NEED_MAPPING) {
                        mappingProgressFilter.needsMapping = true;
                      }

                      viewSettings.update(
                        {
                          ...(viewSettings || {}),
                          filter: {
                            ...viewSettings.filter,
                            ...mappingProgressFilter,
                          },
                        },
                        true,
                      );
                    }}
                    className="dataTableFilter"
                  />
                  <UPAdminDropdownButton
                    title={`Partner Status: ${
                      partnerStatusFilter ? partnerStatusToLabelMapping[partnerStatusFilter] : 'All'
                    }`}
                    options={partnerStatusOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                      selected: option.value === partnerStatusFilter,
                    }))}
                    onClickOption={(partnerStatusValue) => {
                      setPartnerStatusFilter(partnerStatusValue);

                      let partnerStatus;
                      if (partnerStatusValue === UTILITY_PARTNER_STATUS_ACTIVE) {
                        partnerStatus = [
                          UTILITY_PARTNER_STATUS_ACTIVE,
                          UTILITY_PARTNER_STATUS_ONBOARDED,
                        ];
                      } else {
                        partnerStatus = partnerStatusValue;
                      }

                      viewSettings.update(
                        {
                          ...(viewSettings || {}),
                          filter: {
                            ...viewSettings.filter,
                            partnerStatus,
                          },
                        },
                        true,
                      );
                    }}
                    className="dataTableFilter"
                  />
                </SrBox>
                <SrBox marginLeft="auto">
                  <Formik>
                    {(formikBag) => (
                      <SrFormikField
                        formField={{
                          id: 'searchTerm',
                          type: 'short_text',
                          properties: {
                            placeholder: 'Search Address or Partner...',
                            noMargin: true,
                            onChange: (event) => {
                              const searchTerm = (event.target?.value || '')
                                .replace(/[^a-zA-Z0-9 ]/g, '')
                                .trim();

                              const columns =
                                searchTerm.length === 2
                                  ? ['addressStateCode']
                                  : /^\d{5}$/.test(searchTerm)
                                  ? ['addressZip']
                                  : ['addressDisplaySearch', 'partnerName'];

                              const searchFilters = Object.keys(viewSettings.filter).reduce(
                                (memo, key) => {
                                  if (!excludedFilterKeysFromSearch.includes(key)) {
                                    return {
                                      ...memo,
                                      [key]: viewSettings.filter[key],
                                    };
                                  }
                                  return memo;
                                },
                                {},
                              );

                              const updatedFilters = {
                                ...viewSettings.filter,
                                ...initialDataTableFilters,
                                ...searchFilters,
                              };

                              viewSettings.update({
                                ...viewSettings,
                                fuzzy:
                                  searchTerm.length < 2
                                    ? null
                                    : {
                                        queryString: searchTerm,
                                        columns,
                                      },
                                filter: updatedFilters,
                              });
                            },
                            style: {
                              height: '35px',
                              width: '280px',
                            },
                          },
                        }}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    )}
                  </Formik>
                </SrBox>
              </SrFlex>
              <SrFlex marginTop="4px">
                <UPDataTableMappingDefaultsCollapsible
                  title="Mapping Defaults"
                  collapsed
                  utilityTypes={utilityTypes}
                />
              </SrFlex>
            </SrBox>
            <div
              ref={(el) => {
                if (!isLoading && !dataTableApiState.inProgress) {
                  onInfiniteScrollWrapperRef(el);
                }
              }}
            >
              {hitlistTasks.map((hitlistTask) => (
                <SrBox paddingBottom="10px" key={hitlistTask.id}>
                  <UPMappingHitlistRow
                    key={hitlistTask.id}
                    hitlistTask={hitlistTask}
                    currentUser={currentUser}
                    onButtonClick={onButtonClick}
                    onUpdate={() => reloadHitlistTask(hitlistTask.id)}
                    isExpanded={
                      typeof hitlistsExpanded[hitlistTask.id] === 'boolean'
                        ? hitlistsExpanded[hitlistTask.id]
                        : hitlistTask.assignedAdmin?.id === currentUser.id
                    }
                    onRowClick={
                      hitlistTask.assignedAdminUserId !== currentUser.id
                        ? null
                        : (expanded) => {
                            setHitlistsExpanded({
                              ...hitlistsExpanded,
                              [hitlistTask.id]: expanded,
                            });
                          }
                    }
                  />
                </SrBox>
              ))}
              {dataTableApiState.inProgress && <div className="loading-ellipses clearfix" />}
            </div>
          </div>
        );
      }}
    </SrDataTableConnected>
  );
};

UPMappingHitlist.propTypes = {
  currentUser: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  utilityTypes: PropTypes.array.isRequired,
};

export default UPMappingHitlist;
